import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Select, MenuItem, Chip, Alert, CircularProgress,
  Tabs, Tab, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../auth/AuthContext';
import { apiFetch } from '../api';

// ---------------------------------------------------------------------------
// User management (existing)
// ---------------------------------------------------------------------------

interface ManagedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  isActive: boolean;
  lastLogin?: string;
}

const ROLE_OPTIONS = ['user', 'planner', 'admin'];

const ROLE_COLORS: Record<string, string> = {
  admin: '#f72bef',
  planner: '#00b8ff',
  user: '#8a90a0',
};

function UsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadUsers = useCallback(() => {
    setLoading(true);
    apiFetch('/api/auth/users')
      .then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
        setUsers(await res.json());
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const changeRole = async (target: ManagedUser, role: string) => {
    setNotice(null);
    const res = await apiFetch(`/api/auth/users/${target.id}/roles`, {
      method: 'PATCH',
      body: JSON.stringify({ roles: [role] }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setNotice(body.error || 'Failed to update role');
      return;
    }
    setNotice(`${target.email} is now ${role}`);
    loadUsers();
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: '#1a1c26', border: '1px solid #333' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Last Login</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    {u.email}
                    {u.id === user?.id && <Chip label="you" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />}
                  </TableCell>
                  <TableCell>{u.firstName} {u.lastName}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={u.roles[0] || 'user'}
                      onChange={(e) => changeRole(u, e.target.value)}
                      sx={{ minWidth: 110, '& .MuiSelect-select': { py: 0.25, color: ROLE_COLORS[u.roles[0]] || undefined } }}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>{u.lastLogin ? new Date(u.lastLogin).toISOString().slice(0, 16).replace('T', ' ') + 'Z' : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Generic reference-data CRUD grid
// ---------------------------------------------------------------------------

type FieldType = 'text' | 'color' | 'list' | 'number';

type Row = Record<string, any>;

interface ColumnConfig {
  field: string;
  label: string;
  type?: FieldType;
}

interface ResourceConfig {
  label: string;
  /** Plural GET path segment (syncRoutes IDEX_ROUTES key), e.g. "platforms". */
  listPath: string;
  /** Singular write path segment (adminCrudRoutes), e.g. "platform". */
  writePath: string;
  columns: ColumnConfig[];
  /** Synced verbatim from IDEX with no local write endpoint: browse only. */
  readOnly?: boolean;
}

/** Read a possibly dotted field path ("location.lookAtCoordinate") off a row. */
function fieldValue(row: Row, field: string): any {
  return field.split('.').reduce<any>((acc, key) => (acc == null ? undefined : acc[key]), row);
}

const RESOURCES: ResourceConfig[] = [
  {
    label: 'Platforms', listPath: 'platforms', writePath: 'platform',
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'designator', label: 'Designator' },
      { field: 'fullName', label: 'Full Name' },
      { field: 'color', label: 'Color', type: 'color' },
      { field: 'int_disciplines', label: 'INT Disciplines', type: 'list' },
    ],
  },
  {
    label: 'Bases', listPath: 'bases', writePath: 'base',
    columns: [
      { field: 'base_name', label: 'Name' },
      { field: 'base_icao', label: 'ICAO' },
      { field: 'base_coords', label: 'Coordinates' },
    ],
  },
  {
    label: 'Flying Units', listPath: 'flying-units', writePath: 'flying-unit',
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'service', label: 'Service' },
      { field: 'country', label: 'Country' },
      { field: 'ownerOperator', label: 'Owner/Operator' },
    ],
  },
  {
    label: 'COM Units', listPath: 'com-units', writePath: 'com-unit',
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'auth_role', label: 'Auth Role' },
      { field: 'ato_format', label: 'ATO Format' },
      { field: 'ato_day_span_hours', label: 'ATO Day Span (h)', type: 'number' },
    ],
  },
  {
    label: 'PED Units', listPath: 'ped-units', writePath: 'ped-unit',
    columns: [
      { field: 'name', label: 'Name' },
    ],
  },
  {
    label: 'Sensors', listPath: 'sensors', writePath: 'sensor',
    columns: [
      { field: 'sensor_name', label: 'Sensor Name' },
      { field: 'sensor_short_name', label: 'Short Name' },
      { field: 'int_discipline', label: 'INT Discipline' },
    ],
  },
  {
    label: 'Operating Areas', listPath: 'operatingAreas', writePath: 'operatingArea',
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'short_name', label: 'Short Name' },
      { field: 'operation', label: 'Operation' },
      { field: 'combatant_com', label: 'Combatant COM' },
      { field: 'countries', label: 'Countries', type: 'list' },
    ],
  },
  {
    label: 'Word Replacements', listPath: 'word-replacements', writePath: 'word-replacement',
    columns: [
      { field: 'type', label: 'Type' },
      { field: 'original_words', label: 'Original Words', type: 'list' },
      { field: 'replace_with', label: 'Replace With' },
    ],
  },
  // ── Reference data synced verbatim from IDEX (browse only) ──
  {
    label: 'Task Templates', listPath: 'task-templates', writePath: '', readOnly: true,
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'type', label: 'Type' },
      { field: 'operatingArea', label: 'Operating Area' },
      { field: 'primary_int_discipline', label: 'Primary INT' },
      { field: 'secondary_int_discipline', label: 'Secondary INT' },
      { field: 'supported_unit', label: 'Supported Unit' },
    ],
  },
  {
    label: 'Targets', listPath: 'targets', writePath: '', readOnly: true,
    columns: [
      { field: 'target_id', label: 'Target ID' },
      { field: 'name', label: 'Name' },
      { field: 'location.target_type', label: 'Type' },
      { field: 'location.lookAtCoordinate', label: 'Coordinate' },
      { field: 'disabled', label: 'Disabled' },
    ],
  },
  {
    label: 'SRO Areas', listPath: 'sro-areas', writePath: '', readOnly: true,
    columns: [
      { field: 'sro_area', label: 'SRO Area' },
      { field: 'isr_op_area_nickname', label: 'ISR Op Area Nickname' },
    ],
  },
  {
    label: 'Map Layers', listPath: 'layers', writePath: '', readOnly: true,
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'file_name', label: 'File' },
      { field: 'operating_areas', label: 'Operating Areas', type: 'list' },
      { field: 'size', label: 'Size (bytes)', type: 'number' },
    ],
  },
  {
    label: 'TEM Events', listPath: 'tem-events', writePath: '', readOnly: true,
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'description', label: 'Description' },
      { field: 'start', label: 'Start' },
      { field: 'stop', label: 'Stop' },
      { field: 'operating_areas', label: 'Operating Areas', type: 'list' },
    ],
  },
  {
    label: 'Exploitation Nodes', listPath: 'exploitation-nodes', writePath: '', readOnly: true,
    columns: [
      { field: 'name', label: 'Name' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'parentOrg.name', label: 'Parent Org' },
      { field: 'timezoneName', label: 'Timezone' },
      { field: 'deleted', label: 'Deleted' },
    ],
  },
  {
    label: 'JIPCL Sensors', listPath: 'jipcl-sensors', writePath: '', readOnly: true,
    columns: [
      { field: 'sensor_name', label: 'Sensor' },
      { field: 'int_discipline', label: 'INT Discipline' },
    ],
  },
  {
    label: 'Network Types', listPath: 'network-types', writePath: '', readOnly: true,
    columns: [{ field: 'name', label: 'Name' }],
  },
  {
    label: 'Parent Organizations', listPath: 'parent-organizations', writePath: '', readOnly: true,
    columns: [{ field: 'name', label: 'Name' }],
  },
];

function cellValue(row: Row, col: ColumnConfig): React.ReactNode {
  const v = fieldValue(row, col.field);
  if (v === undefined || v === null || v === '') return <span style={{ color: '#555' }}>—</span>;
  if (col.type === 'list') {
    const items = Array.isArray(v) ? v : [v];
    return items.join(', ');
  }
  if (col.type === 'color') {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: String(v), border: '1px solid #333' }} />
        <span>{String(v)}</span>
      </Box>
    );
  }
  return String(v);
}

/** Convert a row value to the string shown in the edit dialog. */
function toEditString(value: any, type: FieldType | undefined): string {
  if (value === undefined || value === null) return '';
  if (type === 'list') return Array.isArray(value) ? value.join(', ') : String(value);
  return String(value);
}

/** Convert an edit-dialog string back into the API value. */
function fromEditString(value: string, type: FieldType | undefined): any {
  const trimmed = value.trim();
  if (type === 'list') {
    return trimmed === '' ? [] : trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (type === 'number') {
    return trimmed === '' ? null : Number(trimmed);
  }
  return trimmed;
}

function ResourceTab({ config, canWrite: canWriteProp }: { config: ResourceConfig; canWrite: boolean }) {
  const canWrite = canWriteProp && !config.readOnly;
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Edit dialog state. editing === null → dialog closed; editing._id absent → create.
  const [editing, setEditing] = useState<Row | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/${config.listPath}`)
      .then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
        setRows(await res.json());
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [config.listPath]);

  useEffect(() => {
    load();
  }, [load]);

  const openDialog = (row: Row | null) => {
    const initial: Record<string, string> = {};
    config.columns.forEach((c) => {
      initial[c.field] = toEditString(row ? row[c.field] : '', c.type);
    });
    setFields(initial);
    setEditing(row ?? {});
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setNotice(null);
    const body: Record<string, any> = {};
    config.columns.forEach((c) => {
      body[c.field] = fromEditString(fields[c.field] ?? '', c.type);
    });
    const isUpdate = !!editing._id;
    const res = await apiFetch(
      isUpdate ? `/api/${config.writePath}/${editing._id}` : `/api/${config.writePath}`,
      { method: isUpdate ? 'PUT' : 'POST', body: JSON.stringify(body) },
    );
    const resBody = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) {
      setNotice(resBody.error || `Save failed (HTTP ${res.status})`);
      return;
    }
    setEditing(null);
    load();
  };

  const remove = async (row: Row) => {
    const name = row[config.columns[0].field] || row._id;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setNotice(null);
    const res = await apiFetch(`/api/${config.writePath}/${row._id}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setNotice(body.error || `Delete failed (HTTP ${res.status})`);
      return;
    }
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {config.label} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>({rows.length})</Typography>
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
        {canWrite && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openDialog(null)}
            sx={{ color: '#f72bef', borderColor: '#f72bef', '&:hover': { borderColor: '#f72bef', bgcolor: 'rgba(247,43,239,0.08)' } }}
          >
            Add
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: '#1a1c26', border: '1px solid #333' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {config.columns.map((c) => (
                  <TableCell key={c.field} sx={{ fontWeight: 600 }}>{c.label}</TableCell>
                ))}
                {canWrite && <TableCell align="right" sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={config.columns.length + (canWrite ? 1 : 0)} sx={{ color: 'text.secondary' }}>
                    No {config.label.toLowerCase()} yet.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  {config.columns.map((c) => (
                    <TableCell key={c.field}>{cellValue(row, c)}</TableCell>
                  ))}
                  {canWrite && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => openDialog(row)} aria-label="edit">
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(row)} aria-label="delete" sx={{ color: '#ff5370' }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: '#1a1c26', border: '1px solid #333' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editing?._id ? `Edit ${config.label.replace(/s$/, '')}` : `Add ${config.label.replace(/s$/, '')}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {config.columns.map((c) => (
            <TextField
              key={c.field}
              size="small"
              label={c.type === 'list' ? `${c.label} (comma-separated)` : c.label}
              type={c.type === 'number' ? 'number' : 'text'}
              value={fields[c.field] ?? ''}
              onChange={(e) => setFields((f) => ({ ...f, [c.field]: e.target.value }))}
              fullWidth
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: '#f72bef', '&:hover': { bgcolor: '#c81ec0' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = !!user?.roles.includes('admin');
  const canWrite = !!user?.roles.some((r) => r === 'admin' || r === 'planner');
  const [tab, setTab] = useState(0);

  // Tab list: Users first (admins only), then the reference-data resources.
  const tabs: Array<{ label: string; render: () => React.ReactNode }> = [];
  if (isAdmin) {
    tabs.push({ label: 'Users', render: () => <UsersTab /> });
  }
  RESOURCES.forEach((r) => {
    tabs.push({ label: r.label, render: () => <ResourceTab config={r} canWrite={canWrite} /> });
  });

  const activeTab = Math.min(tab, tabs.length - 1);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Aussidex Admin Controls</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        View and modify default values and objects within Aussidex. Changes made here are
        reflected in dropdowns and fields throughout the application.
        {!canWrite && ' You are in read-only mode — log in with an admin or planner account to manage data.'}
      </Typography>
      <Paper sx={{ bgcolor: '#1a1c26', border: '1px solid #333', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
            '& .Mui-selected': { color: '#f72bef !important' },
            '& .MuiTabs-indicator': { bgcolor: '#f72bef' },
          }}
        >
          {tabs.map((t) => <Tab key={t.label} label={t.label} />)}
        </Tabs>
      </Paper>
      {tabs[activeTab]?.render()}
    </Box>
  );
}
